import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    measureAgainstContainer: Boolean,
    policy: {
      type: String,
      default: "block"
    }
  };

  connect() {
    if (!this.leadElement) return;

    this.boundUpdateLayout = this.updateLayout.bind(this);
    this.boundCanvasRefreshComplete = this.canvasRefreshComplete.bind(this);
    this.resizeObserver = new ResizeObserver(() => this.scheduleLayoutUpdate());
    this.resizeObserver.observe(this.element);
    if (this.measureAgainstContainerValue && this.measureContainerElement) {
      this.resizeObserver.observe(this.measureContainerElement);
    }

    if (document.fonts?.ready) {
      this.fontReadyPromise = document.fonts.ready.then(() => this.scheduleLayoutUpdate());
    }

    if (document.documentElement.dataset.canvasAuthoritativeRefreshing === "true") {
      document.addEventListener("canvas:authoritative-refresh-complete", this.boundCanvasRefreshComplete, {once: true});
    } else {
      this.updateLayout();
      this.scheduleLayoutUpdate();
    }
  }

  disconnect() {
    cancelAnimationFrame(this.frameRequest);
    window.clearTimeout(this.zeroWidthRetryTimeout);
    this.resizeObserver?.disconnect();
    this.fontReadyPromise = null;
    document.removeEventListener("canvas:authoritative-refresh-complete", this.boundCanvasRefreshComplete);
  }

  canvasRefreshComplete() {
    this.updateLayout();
    this.scheduleLayoutUpdate();
  }

  get leadElement() {
    return this.element.querySelector('[data-inline-tail-role="lead"]');
  }

  get unitElements() {
    return Array.from(this.element.querySelectorAll('[data-inline-tail-role="unit"]'));
  }

  get measureContainerElement() {
    return this.element.closest("[data-inline-tail-measure-container]");
  }

  updateLayout() {
    if (!this.leadElement) return;

    this.applyAvailableWidth();
    this.resetUnitLayout();

    if (this.element.clientWidth === 0) {
      this.scheduleZeroWidthRetry();
      return;
    }

    this.resolveOwnLineUnits();
    this.updateRootState();
    this.element.dataset.inlineTailReady = "true";
  }

  applyAvailableWidth() {
    if (!this.measureAgainstContainerValue) {
      this.element.style.removeProperty("--inline-tail-available-width");
      return;
    }

    const container = this.measureContainerElement;
    const rect = this.element.getBoundingClientRect();
    const containerRect = container?.getBoundingClientRect();

    if (!containerRect || rect.width === 0) {
      this.element.style.removeProperty("--inline-tail-available-width");
      return;
    }

    const availableWidth = Math.max(0, containerRect.right - rect.left);
    this.element.style.setProperty("--inline-tail-available-width", `${availableWidth}px`);
  }

  resetUnitLayout() {
    this.unitElements.forEach((unit) => {
      unit.dataset.inlineTailLine = "shared";
    });
  }

  resolveOwnLineUnits() {
    const units = this.unitElements;
    const maxPasses = units.length + 1;

    for (let pass = 0; pass < maxPasses; pass += 1) {
      let changed = false;

      units.forEach((unit, index) => {
        const previousElement = index === 0 ? this.leadElement : units[index - 1];
        if (!previousElement) return;

        const ownLine = this.startsOwnLine(unit, previousElement);
        const nextValue = ownLine ? "own" : "shared";

        if (unit.dataset.inlineTailLine !== nextValue) {
          unit.dataset.inlineTailLine = nextValue;
          changed = true;
        }
      });

      if (!changed) break;
    }
  }

  startsOwnLine(unit, previousElement) {
    const unitRect = unit.getBoundingClientRect();
    const previousRect = previousElement.getBoundingClientRect();
    const container = this.measureAgainstContainerValue ? this.measureContainerElement : this.element;
    const containerRect = container?.getBoundingClientRect();

    return (
      unitRect.top >= previousRect.bottom - 1 ||
      this.requiresOwnLineForSharedWidth(unit, previousElement, containerRect) ||
      (containerRect && unitRect.right > containerRect.right + 1)
    );
  }

  requiresOwnLineForSharedWidth(unit, previousElement, containerRect) {
    if (!containerRect) return false;

    const previousRect = previousElement.getBoundingClientRect();
    const availableWidth = Math.max(0, containerRect.right - previousRect.right);

    return this.measureUnwrappedWidth(unit) > availableWidth + 1;
  }

  measureUnwrappedWidth(unit) {
    const clone = unit.cloneNode(true);
    clone.style.position = "absolute";
    clone.style.left = "-9999px";
    clone.style.top = "-9999px";
    clone.style.visibility = "hidden";
    clone.style.pointerEvents = "none";
    clone.style.maxWidth = "none";
    clone.style.width = "max-content";
    clone.style.whiteSpace = "nowrap";
    clone.querySelectorAll('[data-inline-tail-role="content"]').forEach((content) => {
      content.style.overflowWrap = "normal";
      content.style.whiteSpace = "nowrap";
      content.style.wordBreak = "normal";
    });

    document.body.appendChild(clone);
    const width = clone.getBoundingClientRect().width;
    clone.remove();
    return width;
  }

  updateRootState() {
    const hasOwnLineUnit = this.unitElements.some((unit) => unit.dataset.inlineTailLine === "own");

    this.element.dataset.inlineTailState = hasOwnLineUnit ? "has-own-line" : "all-shared";
    this.element.dataset.inlineTailPolicy = this.policyValue;
  }

  scheduleLayoutUpdate() {
    cancelAnimationFrame(this.frameRequest);
    this.frameRequest = requestAnimationFrame(this.boundUpdateLayout);
  }

  scheduleZeroWidthRetry() {
    if (this.zeroWidthRetryTimeout) return;

    this.zeroWidthRetryTimeout = window.setTimeout(() => {
      this.zeroWidthRetryTimeout = null;
      this.scheduleLayoutUpdate();
    }, 50);
  }
}
